import axios from 'axios';

import type { LoginStep } from './loginStateMachine';

/**
 * Map a server error (or network failure) onto an i18n key + optional
 * retry hint. Pure function — no dispatch, no toast side effects — so it
 * can be unit-tested without React.
 *
 * AUT-110 wire codes (collapsed from the AUT-108 / AUT-109 backend
 * registry):
 *   - `admin_invalid_credentials` (401)  → `auth.errors.invalidCredentials`
 *   - `admin_account_locked`      (423)  → `auth.errors.accountLocked`
 *   - `admin_phone_login_disabled`(403)  → `auth.errors.phoneLoginDisabled`
 *   - any 4xx/5xx on TOTP step           → `auth.errors.invalidTotp`
 *   - 429 (no specific code)             → `auth.errors.rateLimit`
 *                                          (+ retryAfterSeconds from header)
 *   - 5xx                                → `auth.errors.server`
 *   - axios w/o .response                → `auth.errors.network`
 *   - anything else                      → `auth.errors.unknown`
 *
 * The status-code path runs FIRST for 423 (locked) and 403
 * (phone-login-disabled) so the anti-enum 401 invalid-credentials path
 * doesn't shadow them — they carry richer UX implications (wait period
 * + "use email login").
 */
export type MappedLoginError = {
  key: string;
  retryAfterSeconds?: number;
};

export function mapLoginError(err: unknown, step: LoginStep): MappedLoginError {
  if (!axios.isAxiosError(err) || !err.response) {
    return { key: 'auth.errors.network' };
  }
  const status = err.response.status;
  const data = err.response.data as { code?: string } | undefined;
  const code = data?.code;

  // 429 reads Retry-After; collapse to rateLimit on every step.
  if (status === 429) {
    const retryHeader = err.response.headers['retry-after'];
    const retryAfter = retryHeader ? parseInt(String(retryHeader), 10) : 60;
    return {
      key: 'auth.errors.rateLimit',
      retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : 60,
    };
  }

  // 423 — account-locked. Bypasses the anti-enum 401 so the SPA can
  // render the "wait 15 minutes" hint.
  if (status === 423 || code === 'admin_account_locked') {
    return { key: 'auth.errors.accountLocked' };
  }

  // 403 — phone-login-disabled. Defensive: the SPA never POSTs phone OTP
  // for admins, but if a stray call hits, surface the explanatory copy.
  if (code === 'admin_phone_login_disabled') {
    return { key: 'auth.errors.phoneLoginDisabled' };
  }

  // 401 — invalid credentials anti-enum (5 reject branches collapse here).
  if (status === 401 || code === 'admin_invalid_credentials') {
    if (step === 'totp') return { key: 'auth.errors.invalidTotp' };
    return { key: 'auth.errors.invalidCredentials' };
  }

  // 400 / 422 on TOTP step — bad TOTP code.
  if ((status === 400 || status === 422) && step === 'totp') {
    return { key: 'auth.errors.invalidTotp' };
  }

  if (status === 403 && code === 'not_admin') {
    return { key: 'auth.errors.notAdmin' };
  }
  if (status === 409 && code === 'role_conflict') {
    return { key: 'auth.errors.roleConflict' };
  }
  if (status >= 500) {
    return { key: 'auth.errors.server' };
  }
  return { key: 'auth.errors.unknown' };
}
